import { type UiLibI18n } from '@myre/domain-i18n-wordings';
import { CopierService } from '@myre/ui-primitive-core';

import { Component, inject, input, signal } from '@angular/core';
import { type SafeHtml } from '@angular/platform-browser';

import { WORDINGS } from '../../../myre-wordings-loader';

/**
 * Renders one self-contained block the agent marked as independently copyable
 * (via a ```copy fence). The block shows its rendered markdown plus its own
 * copy button, so a single answer can offer several pieces of content — e.g.
 * three draft emails — each copyable on its own.
 */
@Component({
  selector: 'app-copyable-block',
  standalone: true,
  templateUrl: './copyable-block.component.html',
  styleUrl: './copyable-block.component.scss',
})
export class CopyableBlockComponent {
  private readonly _copierService = inject(CopierService);
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  /** Raw text copied to the clipboard verbatim. */
  public readonly text = input.required<string>();

  /** Rendered markdown of the block, shown to the user. */
  public readonly html = input.required<SafeHtml>();

  /** Bubble variant ('user' | 'assistant'), drives the card's colour scheme. */
  public readonly variant = input<string>('assistant');

  protected get chatWordings() {
    return this._wordings._library.chat;
  }

  /** Transient "copied" confirmation state, reset shortly after a copy. */
  protected readonly _copied = signal(false);
  private _resetTimer: ReturnType<typeof setTimeout> | null = null;

  protected copy(): void {
    if (!this._copierService.copyText(this.text())) {
      return;
    }

    this._copied.set(true);

    if (this._resetTimer !== null) {
      clearTimeout(this._resetTimer);
    }

    this._resetTimer = setTimeout(() => {
      this._copied.set(false);
      this._resetTimer = null;
    }, 1500);
  }
}
